import React, { useState, useEffect } from 'react';
import { X, Check } from 'lucide-react';
import { Spinner } from '../../../../components/Common/Loader';

export interface SalesOrgRecord {
  id: number;
  orgCode?: string;
  name: string;
  description?: string;
  status: 'Active' | 'Inactive';
  createdOn: string;
  createdBy: string;
}

export interface SalesOrgFormData {
  id?: number;
  name: string;
  description: string;
  status: 'Active' | 'Inactive';
}

interface AddEditSalesOrganizationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (record: SalesOrgFormData) => Promise<void> | void;
  initialData?: SalesOrgRecord | null;
  isSubmitting?: boolean;
}

export const AddEditSalesOrganizationModal: React.FC<AddEditSalesOrganizationModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialData,
  isSubmitting = false,
}) => {
  const isEditing = Boolean(initialData);

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<'Active' | 'Inactive'>('Active');
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    queueMicrotask(() => {
      if (initialData) {
        setName(initialData.name || '');
        setDescription(initialData.description || '');
        setStatus(initialData.status || 'Active');
      } else {
        setName('');
        setDescription('');
        setStatus('Active');
      }
      setFormError(null);
    });
  }, [initialData, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setFormError('Please enter an organization name.');
      return;
    }

    try {
      setFormError(null);
      await onSave({
        id: initialData?.id,
        name: name.trim(),
        description: description.trim(),
        status,
      });
    } catch (err: any) {
      setFormError(err?.message || 'Failed to save sales organization');
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl max-w-md w-full p-6 sm:p-8 shadow-2xl border border-slate-100 animate-fadeIn space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-slate-100">
          <div>
            <h3 className="text-lg font-extrabold text-slate-800 tracking-tight">
              {isEditing ? 'Edit Sales Organization' : 'Add Sales Organization'}
            </h3>
            <p className="text-xs font-medium text-slate-400 mt-0.5">
              {isEditing ? 'Update organization details' : 'Fill details to create sales organization'}
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500 flex items-center justify-center cursor-pointer transition-colors disabled:opacity-50"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {formError && (
          <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-700 text-xs font-semibold">
            {formError}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4 text-xs">
          {/* Org Name */}
          <div className="space-y-1">
            <label className="font-bold text-slate-700">Organization Name *</label>
            <input
              type="text"
              required
              disabled={isSubmitting}
              placeholder="e.g. Western Region Sales Org"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#C1272D] disabled:opacity-60"
            />
          </div>

          {/* Description */}
          <div className="space-y-1">
            <label className="font-bold text-slate-700">Description</label>
            <input
              type="text"
              disabled={isSubmitting}
              placeholder="e.g. Handles Western region sales distribution"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-slate-800 placeholder-slate-400 focus:outline-none focus:border-[#C1272D] disabled:opacity-60"
            />
          </div>

          {/* Status Radio options */}
          <div className="space-y-1 pt-1">
            <label className="font-bold text-slate-700 block">Status</label>
            <div className="flex items-center gap-6 pt-1">
              <label className="flex items-center gap-2 cursor-pointer font-semibold text-slate-700">
                <input
                  type="radio"
                  name="orgStatus"
                  value="Active"
                  disabled={isSubmitting}
                  checked={status === 'Active'}
                  onChange={() => setStatus('Active')}
                  className="accent-[#C1272D]"
                />
                <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-md text-[11px] font-bold">
                  Active
                </span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer font-semibold text-slate-700">
                <input
                  type="radio"
                  name="orgStatus"
                  value="Inactive"
                  disabled={isSubmitting}
                  checked={status === 'Inactive'}
                  onChange={() => setStatus('Inactive')}
                  className="accent-[#C1272D]"
                />
                <span className="px-2 py-0.5 bg-rose-100 text-rose-700 rounded-md text-[11px] font-bold">
                  Inactive
                </span>
              </label>
            </div>
          </div>

          {/* Buttons Footer */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2.5 border border-slate-200 text-slate-600 font-bold rounded-xl hover:bg-slate-50 cursor-pointer transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-5 py-2.5 bg-[#C1272D] hover:bg-[#A81E23] text-white font-bold rounded-xl shadow-md shadow-[#C1272D]/20 cursor-pointer flex items-center gap-1.5 transition-all disabled:opacity-50 active:scale-95"
            >
              {isSubmitting ? (
                <>
                  <Spinner size="sm" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  <span>{isEditing ? 'Save Changes' : 'Add Organization'}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
